import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaFilter } from 'react-icons/fa';
import ProductCard from '../products/ProductCard';
import { useTheme } from '../../context/ThemeContext';

const ProductSection = ({
  title,
  subtitle,
  products,
  showFilter = false,
  columns = 4,
  showViewAll = true,
  selectedCategory,
  setSelectedCategory
}) => {

  const { isDarkMode } = useTheme();

  const [sortBy, setSortBy] = useState('featured');

  // CATEGORY LIST
  const categories = [
    'all',
    'Gold',
    'Diamond',
    'Silver',
    'Platinum'
  ];

  // SORT OPTIONS
  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Customer Rating' },
    { value: 'newest', label: 'Newest First' }
  ];

  // SORT PRODUCTS
  let filteredProducts = [...products].sort((a, b) => {

    switch (sortBy) {

      case 'price-low':
        return a.price - b.price;

      case 'price-high':
        return b.price - a.price;

      case 'rating':
        return (b.rating || 0) - (a.rating || 0);

      case 'newest':
        return b.id - a.id;

      default:
        return 0;
    }
  });

  return (
    <section
      className="section-padding"
      style={{
        background: isDarkMode
          ? 'var(--bg-primary)'
          : 'white'
      }}
    >

      <div className="container">

        {/* SECTION HEADER */}
        <motion.div
          className="text-center mb-5"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >

          {subtitle && (

            <p
              className="text-uppercase mb-2"
              style={{
                color: 'var(--primary-gold)',
                letterSpacing: '2px',
                fontWeight: '500',
                fontSize: '14px'
              }}
            >
              {subtitle}
            </p>
          )}

          <h2
            className="display-5 mb-3"
            style={{
              fontFamily: 'var(--font-secondary)',
              fontWeight: '700',
              color: 'var(--text-primary)'
            }}
          >
            {title}
          </h2>

          <div className="d-flex justify-content-center mb-4">

            <div
              style={{
                width: '80px',
                height: '3px',
                background: 'var(--gradient-gold)',
                borderRadius: '2px'
              }}
            />

          </div>

        </motion.div>

        {/* FILTER BAR */}
        {showFilter && (

          <motion.div
            className="mb-4"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >

            <div className="row g-3 align-items-center">

              {/* CATEGORY FILTER */}
              <div className="col-lg-8">

                <div className="d-flex flex-wrap gap-2">

                  {categories.map((category) => (

                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`btn ${selectedCategory === category
                          ? 'btn-luxury'
                          : 'btn-outline-secondary'
                        }`}
                      style={{
                        borderRadius: '25px',
                        padding: '8px 20px',
                        fontWeight: '500',
                        fontSize: '14px',
                        textTransform: 'capitalize'
                      }}
                    >
                      {category}
                    </button>

                  ))}

                </div>

              </div>

              {/* SORT DROPDOWN */}
              <div className="col-lg-4">

                <div className="d-flex align-items-center justify-content-lg-end gap-3">

                  <FaFilter
                    style={{
                      color: 'var(--text-secondary)'
                    }}
                  />

                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      borderRadius: '25px',
                      padding: '8px 20px',
                      border: '1px solid var(--border-color)',
                      background: isDarkMode
                        ? 'var(--bg-secondary)'
                        : 'white',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >

                    {sortOptions.map(option => (

                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>

                    ))}

                  </select>

                </div>

              </div>

            </div>

          </motion.div>
        )}

        {/* PRODUCTS GRID */}
        <div className="row g-4 mb-5">

          {filteredProducts.map((product, index) => (

            <div
              key={product.id}
              className={`col-12 col-sm-6 col-md-4 ${columns === 4
                  ? 'col-lg-3'
                  : 'col-lg-4'
                }`}
            >

              <ProductCard
                product={product}
                index={index}
              />

            </div>

          ))}

        </div>

        {/* VIEW ALL BUTTON */}
        {showViewAll && (

          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >

            <Link
              to="/collections"
              className="btn btn-luxury btn-lg d-inline-flex align-items-center"
            >
              View All Products
              <FaArrowRight className="ms-2" />
            </Link>

          </motion.div>
        )}

        {/* NO PRODUCTS */}
        {filteredProducts.length === 0 && (

          <div className="text-center py-5">

            <p className="text-muted">
              No products found in this category.
            </p>

          </div>
        )}

      </div>

    </section>
  );
};

export default ProductSection;